import _ from 'lodash'
import { type ReactNode } from 'react'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { ProfilePagination } from '@/components/profile/ProfilePagination'
import { ORIGINAL_BET_HISTORY_PER_PAGE } from '@/config/original'
import { formatDisplayDate } from '@/lib/format-date'
import { formatSignedAmount } from '@/lib/format-number'
import { cn } from '@/lib/utils'
import type { OriginalBetHistoryItem } from '@/types/original'

export interface OriginalBetHistoryColumn {
	key: string
	header: string
	className?: string
	render: (item: OriginalBetHistoryItem) => ReactNode
}

export const DEFAULT_ORIGINAL_BET_HISTORY_COLUMNS: OriginalBetHistoryColumn[] = [
	{
		key: 'bet',
		header: 'Bet',
		render: (item) => <span className='font-medium text-foreground'>{formatSignedAmount(item.bet_amount)}</span>,
	},
	{
		key: 'multiplier',
		header: 'Multiplier',
		render: (item) => <span className='text-muted-foreground'>{item.multiplier || '-'}</span>,
	},
	{
		key: 'payout',
		header: 'Payout',
		render: (item) => <span className={cn('font-semibold', item.result === 'win' ? 'text-success' : 'text-foreground')}>{formatSignedAmount(item.payout)}</span>,
	},
	{
		key: 'result',
		header: 'Result',
		render: (item) => (
			<span
				className={cn(
					'inline-flex rounded-md px-2 py-0.5 text-xs font-semibold capitalize',
					item.result === 'win' && 'bg-success/15 text-success',
					item.result === 'loss' && 'bg-destructive/15 text-destructive',
					item.result === 'pending' && 'bg-secondary text-muted-foreground',
				)}
			>
				{item.result}
			</span>
		),
	},
	{
		key: 'date',
		header: 'Date',
		className: 'text-muted-foreground',
		render: (item) => formatDisplayDate(item.created_at),
	},
]

interface OriginalBetHistoryProps {
	bets: OriginalBetHistoryItem[]
	columns?: OriginalBetHistoryColumn[]
	isLoading?: boolean
	isFetching?: boolean
	isError?: boolean
	page: number
	total: number
	onPageChange: (page: number) => void
	emptyMessage?: string
}

function BetHistorySkeletonRows({ columnCount }: { columnCount: number }) {
	return (
		<>
			{_.times(5, (index) => (
				<TableRow key={`bet-history-skeleton-${index}`} className='border-border/60'>
					{_.times(columnCount, (cellIndex) => (
						<TableCell key={cellIndex} className='py-3'>
							<Skeleton className='h-4 w-full max-w-[120px]' />
						</TableCell>
					))}
				</TableRow>
			))}
		</>
	)
}

export function OriginalBetHistory({
	bets,
	columns = DEFAULT_ORIGINAL_BET_HISTORY_COLUMNS,
	isLoading = false,
	isFetching = false,
	isError = false,
	page,
	total,
	onPageChange,
	emptyMessage = 'No bets placed yet.',
}: OriginalBetHistoryProps) {
	return (
		<section className='overflow-hidden rounded-2xl border border-border bg-card shadow-[0_4px_12px_0_hsl(var(--background)/0.2)]'>
			<div className='px-4 py-4 sm:px-6'>
				<h2 className='text-lg font-semibold tracking-[-0.27px] text-foreground'>Bet History</h2>
			</div>

			<Table>
				<TableHeader>
					<TableRow className='border-border bg-secondary hover:bg-secondary'>
						{_.map(columns, (column) => (
							<TableHead key={column.key} className='h-11 text-muted-foreground'>
								{column.header}
							</TableHead>
						))}
					</TableRow>
				</TableHeader>
				<TableBody className={cn(isFetching && !isLoading && 'opacity-70')}>
					{isLoading ? <BetHistorySkeletonRows columnCount={columns.length} /> : null}
					{!isLoading && isError ? (
						<TableRow>
							<TableCell colSpan={columns.length} className='py-10 text-center text-sm text-destructive'>
								Unable to load bet history.
							</TableCell>
						</TableRow>
					) : null}
					{!isLoading && !isError && _.isEmpty(bets) ? (
						<TableRow>
							<TableCell colSpan={columns.length} className='py-10 text-center text-sm text-muted-foreground'>
								{emptyMessage}
							</TableCell>
						</TableRow>
					) : null}
					{!isLoading && !isError
						? _.map(bets, (bet) => (
								<TableRow key={bet.id} className='border-border/60 hover:bg-accent/30'>
									{_.map(columns, (column) => (
										<TableCell key={column.key} className={cn('py-3', column.className)}>
											{column.render(bet)}
										</TableCell>
									))}
								</TableRow>
							))
						: null}
				</TableBody>
			</Table>

			<ProfilePagination page={page} perPage={ORIGINAL_BET_HISTORY_PER_PAGE} total={total} onPageChange={onPageChange} />
		</section>
	)
}
